import React, { useEffect, useRef, useMemo } from 'react';
import { PropTypes } from 'prop-types';

import DatePicker from 'react-datepicker';
import moment from 'moment';
import isValid from 'date-fns/isValid';

import DateTimes from 'components/Common/DateTimes';

const DateFilter = ({ column: { filterValue, setFilter, preFilteredRows, id, filterIconId }, dateFormat, popperPlacement, popperModifiers }) => {
  const [inputValue, setInputValue] = React.useState(null);
  const [showFilter, setShowFilter] = React.useState(false);
  const [overflow, setOverflow] = React.useState(false);
  const node = useRef();

  const handleClickOutside = (e) => {
    if (node === null || node.current === null) {
      return;
    }

    if (node.current.contains(e.target)) {
      // inside click
      return;
    }

    setShowFilter(false);
  };

  const GetDate = (dateString) => {
    if (dateString !== null && dateString !== undefined) {
      return moment(dateString, dateFormat.toUpperCase()).toDate();
    }
    return null;
  };

  const [minDate, maxDate] = useMemo(() => {
    let minVal = null;
    let maxVal = null;

    if (preFilteredRows && preFilteredRows.length) {
      const baseDate = GetDate(preFilteredRows[0]);
      minVal = isValid(baseDate) ? baseDate : new Date(0);
      maxVal = isValid(baseDate) ? baseDate : GetDate(DateTimes.today);
      preFilteredRows.forEach((row) => {
        const rowDate = moment(row);
        minVal = rowDate <= minVal ? rowDate : minVal;
        maxVal = rowDate >= maxVal ? rowDate : maxVal;
      });
    }

    return [minVal, maxVal];
  }, [id, preFilteredRows]);

  const clear = () => {
    setShowFilter(false);
    setOverflow(false);
    setInputValue(null);
    setFilter({ filter: null, filterType: 'date' });
  };

  useEffect(() => {
    setInputValue(filterValue && filterValue.filter ? filterValue.filter : null);
  }, [filterValue]);

  useEffect(() => {
    if (showFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilter]);

  return (
    <div
      ref={ node }
      className={ `clickover clickover-bottom filter ${ showFilter ? 'active' : '' }` }
    >
      <button
        type="button"
        onClick={ () => setShowFilter(!showFilter) }
        className={ `btn btn-link filter-menu ${ inputValue !== null ? 'filter-active' : '' }` }
      >
        ☰
      </button>
      <div id={ filterIconId ?? null } className={ `clickover-container ${ overflow ? 'make-visible' : '' }` }>
        <div className="card card-primary">
          <div className="card-body">
            <div className="columns">
              <div className="col-12 date_filter">
                <div className="column">
                  <div className="col-3 df_item lb">Date: </div>
                  <div className="col-9 df_item">
                    <DatePicker
                      selected={ GetDate(inputValue) }
                      minDate={ GetDate(minDate) }
                      maxDate={ GetDate(maxDate) }
                      dateFormat={ dateFormat }
                      placeholderText={ dateFormat.toUpperCase() }
                      onCalendarOpen={ () => setOverflow(true) }
                      onCalendarClose={ () => setOverflow(false) }
                      onChange={ (e) => {
                        const val = GetDate(e);
                        setInputValue(val);
                      } }
                      popperPlacement={ popperPlacement }
                      popperModifiers={ popperModifiers ?? [{ name: 'flip', enabled: false }] }
                    />
                  </div>
                </div>
              </div>
              <div className="col-6">
                <button
                  id="search-btn"
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={ () => {
                    setShowFilter(false);
                    setFilter({ filter: inputValue, filterType: 'date' });
                  } }
                >
                  <span className="icon icon-search" /> Filter
                </button>
              </div>
              <div className="col-6">
                <button
                  id="clear-btn"
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={ inputValue === null }
                  onClick={ () => { clear(); } }
                >
                  <span className="icon icon-cross" /> Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

DateFilter.propTypes = {
  column: PropTypes.shape().isRequired,
  filterIconId: PropTypes.string,
  dateFormat: PropTypes.string,
  popperPlacement: PropTypes.string,
  popperModifiers: PropTypes.shape({}),
};

DateFilter.defaultProps = {
  filterIconId: null,
  dateFormat: 'yyyy-MM-dd',
  popperPlacement: 'bottom-start',
  popperModifiers: null,
};

export default DateFilter;
